import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type Dispatch,
    type ReactNode,
    type SetStateAction,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { ActionBar, type ActionBarSection } from '../ActionBar';
import { SegmentedControl } from '../SegmentedControl';
import { Loader } from '../Loader';

import { applyDataViewFilters } from './applyFilters';
import { DataViewContext } from './DataViewContext';
import { FilterChip, buildExtraFilterConfig, getExtraFilterTitle } from './FilterChip';
import { useFilterState, type FilterDescriptor } from './useFilterState';
import type { DataViewActionSection, DataViewFilter, DataViewMode, DataViewSelection } from './types';

export type { DataViewFilter, DataViewMode, DataViewActionSection, DataViewSelection } from './types';
export type { DataViewPopupProps, DataViewAction } from './types';

const VIEW_PARAM = 'view';

type DataViewProps<TData> = {
    data: TData[];
    getRowId: (row: TData) => string;
    /** Déclarés une fois, partagés par toutes les vues. `accessor` ⇒ filtré ici ; sinon filtre serveur. */
    filters: DataViewFilter<TData>[];
    search?: {
        placeholder?: string;
        /**
         * Les champs d'une ligne où chercher ⇒ la `DataView` filtre les lignes chargées.
         *
         * ABSENT ⇒ recherche SERVEUR : la valeur est remontée par `onFiltersChange` comme n'importe
         * quel filtre sans accessor. C'est le seul mode correct sur une liste paginée — chercher dans
         * les lignes déjà chargées ne regarderait que la page en cours.
         */
        accessor?: (row: TData) => (string | null | undefined)[];
    };
    views: DataViewMode<TData>[];
    actions?: DataViewActionSection<TData>[];
    /** Remonte l'état des filtres à la page, pour ceux qui pilotent la query (filtres sans `accessor`). */
    onFiltersChange?: (filters: Record<string, unknown>) => void;
    /**
     * Un bandeau propre à la page, entre les filtres et la vue. Il reçoit les lignes FILTRÉES et l'API
     * de sélection : il vaut donc pour toutes les vues, aucune n'a à le recopier.
     */
    renderSummary?: (params: { rows: TData[]; selection: DataViewSelection }) => ReactNode;
    isLoading?: boolean;
    disableUrlSync?: boolean;
};

/**
 * La coquille des pages à plusieurs vues : elle porte les filtres, la recherche, la synchro URL, la
 * sélection, le sélecteur de vue et la barre d'actions. Les vues ne sont que des rendus — une
 * `DataTable`, un kanban, un calendrier — et n'ont rien à savoir les unes des autres.
 *
 * Tout se déclare en config, comme les `columns` d'une `DataTable` : aucun composant intermédiaire
 * par page.
 */
export function DataView<TData>({
    data,
    getRowId,
    filters,
    search,
    views,
    actions,
    onFiltersChange,
    renderSummary,
    isLoading = false,
    disableUrlSync = false,
}: DataViewProps<TData>) {
    const [urlSearchParams] = useSearchParams();

    const [viewKey, setViewKey] = useState<string>(() => {
        const fromUrl = urlSearchParams.get(VIEW_PARAM);
        return fromUrl && views.some((view) => view.key === fromUrl) ? fromUrl : views[0].key;
    });

    const descriptors = useMemo<FilterDescriptor[]>(
        () => filters.map(({ key, type, defaultValue }) => ({ key, type, defaultValue })),
        [filters],
    );

    const extraParams = useMemo(() => ({ [VIEW_PARAM]: viewKey }), [viewKey]);

    const { filterState, searchValue, searchKey, updateFilter, updateSearch, clearFilters } = useFilterState<
        Record<string, unknown>
    >({
        filters: descriptors,
        enableUrlSync: !disableUrlSync,
        extraParams,
    });

    // Les filtres sans `accessor` deviennent des variables de query : notifier à chaque frappe ferait un
    // aller-retour par lettre. Même fenêtre que la `DataTable` en mode serveur (300 ms). La ref garde la
    // fonction débouncée stable quand la page reconstruit son `onFiltersChange`.
    const onFiltersChangeRef = useRef(onFiltersChange);
    onFiltersChangeRef.current = onFiltersChange;

    const notifyFiltersChange = useMemo(
        () => debounce((filters: Record<string, unknown>) => onFiltersChangeRef.current?.(filters), 300),
        [],
    );

    useEffect(() => {
        notifyFiltersChange(filterState);
        return () => notifyFiltersChange.cancel();
    }, [filterState, notifyFiltersChange]);

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const toggleKey = useCallback((key: string) => {
        setSelectedKeys((current) =>
            current.includes(key) ? current.filter((candidate) => candidate !== key) : [...current, key],
        );
    }, []);
    const selection: DataViewSelection = useMemo(
        () => ({ selectedKeys, setSelectedKeys, toggleKey }),
        [selectedKeys, toggleKey],
    );

    const rows = useMemo(
        () =>
            applyDataViewFilters({
                rows: data,
                filters,
                filterState,
                searchValue,
                searchAccessor: search?.accessor,
            }),
        [data, filters, filterState, searchValue, search],
    );

    const selectedRows = useMemo(() => {
        const keys = new Set(selectedKeys);
        return rows.filter((row) => keys.has(getRowId(row)));
    }, [rows, selectedKeys, getRowId]);

    const [openActionKey, setOpenActionKey] = useState<string | null>(null);
    const allActions = useMemo(() => (actions ?? []).flatMap((section) => section.actions), [actions]);

    // Une popup reste montée en permanence avec `isOpen` à faux, comme quand chaque page tenait son
    // propre booléen : c'est ce qui laisse jouer les animations de fermeture.
    const makeSetIsOpen = useCallback(
        (key: string): Dispatch<SetStateAction<boolean>> =>
            (valueOrUpdater) => {
                setOpenActionKey((current) => {
                    const wasOpen = current === key;
                    const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(wasOpen) : valueOrUpdater;
                    if (next) return key;
                    return wasOpen ? null : current;
                });
            },
        [],
    );

    const actionSections: ActionBarSection[] = useMemo(
        () =>
            (actions ?? []).map((section) => ({
                title: section.title,
                actions: section.actions.map((action) => ({
                    icon: action.icon,
                    label: action.label,
                    variant: action.variant,
                    dataTest: action.dataTest,
                    requiredPermissions: action.requiredPermissions,
                    hidden: action.visibleWhen ? !action.visibleWhen(selectedRows) : false,
                    disabled: action.disabledWhen ? action.disabledWhen(selectedRows) : false,
                    onClick: () => {
                        if (action.onClick) {
                            action.onClick(selectedRows, selection);
                            return;
                        }
                        setOpenActionKey(action.key);
                    },
                })),
            })),
        [actions, selectedRows, selection],
    );

    const hasActiveFilters = useMemo(
        () =>
            Object.entries(filterState).some(([key, value]) => {
                if (key === searchKey || value === undefined) return false;
                const declared = filters.find((filter) => filter.key === key);
                return JSON.stringify(value) !== JSON.stringify(declared?.defaultValue);
            }),
        [filterState, filters, searchKey],
    );

    const currentView = views.find((view) => view.key === viewKey) ?? views[0];

    return (
        <DataViewContext.Provider
            value={{
                filters: { filterState, searchValue, searchKey, updateFilter, updateSearch, clearFilters },
            }}
        >
            <div className="flex h-full min-h-0 flex-col">
                <div className="flex flex-col gap-2 border-b border-border px-4 py-3">
                    {search && (
                        <Input
                            placeholder={search.placeholder ?? 'Recherche...'}
                            value={searchValue}
                            onChange={(event) => updateSearch(event.target.value)}
                            className="h-8 w-full"
                            data-test="data-view-search"
                        />
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                        {filters.map((filter) => (
                            <FilterChip
                                key={filter.key}
                                config={buildExtraFilterConfig<Record<string, unknown>>(filter)}
                                title={getExtraFilterTitle(filter)}
                                value={filterState[filter.key]}
                                onChange={(value) => updateFilter(filter.key, value)}
                            />
                        ))}

                        {hasActiveFilters && (
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8"
                                onClick={clearFilters}
                                data-test="data-view-clear-filters"
                            >
                                Effacer
                                <X className="ml-1 size-3.5" />
                            </Button>
                        )}

                        <div className="ml-auto flex items-center gap-2">
                            {views.length > 1 && (
                                <SegmentedControl
                                    options={views.map(({ key, label }) => ({ value: key, label }))}
                                    value={currentView.key}
                                    onChange={setViewKey}
                                />
                            )}
                        </div>
                    </div>
                </div>

                {renderSummary?.({ rows, selection })}

                <div className="relative flex min-h-0 flex-1">
                    {isLoading ? <Loader /> : currentView.render({ rows, selection })}
                    {actionSections.length > 0 && (
                        <ActionBar actionSections={actionSections} isInitiallyOpen={selectedKeys.length > 0} />
                    )}
                </div>
            </div>

            {allActions.map((action) =>
                action.renderPopup ? (
                    <Fragment key={action.key}>
                        {action.renderPopup({
                            isOpen: openActionKey === action.key,
                            setIsOpen: makeSetIsOpen(action.key),
                            rows,
                            selectedKeys,
                            setSelectedKeys,
                        })}
                    </Fragment>
                ) : null,
            )}
        </DataViewContext.Provider>
    );
}
